import { useEffect, useRef } from 'react';
import { useAuth } from '@neotech-solutions-org/neofusion-fe-shared';
import { QUERY_KEYS } from '../../../constants';
import {
  FINPSP_DEPOSIT_TYPE,
  FINPSP_PAYMENT_QUERY_KEYS,
  FINPSP_PAYMENT_STATUS,
  FINPSP_PAYMENT_STATE,
  FINPSP_PAYMENT_POLL_INTERVAL,
} from '../../../constants/finpsp';
import { useFinpspPayment } from '../../../queries/finpsp';
import { useInvalidateQuery } from '../../useInvalidateQuery';
import { useExtendedSnackbar } from '../../useExtendedSnackbar';
import useLocalization from '../../useLocalization';
import { getFinpspCheckoutUrl, getFinpspDepositType, isFinpspTerminal } from '../../../helpers/payment/finpsp';

export const useFinpspPaymentInstructions = (transactionId: string) => {
  const { isAuthenticated } = useAuth();
  const query = useFinpspPayment(transactionId, FINPSP_PAYMENT_POLL_INTERVAL);
  const invalidate = useInvalidateQuery();
  const refreshed = useRef<string | null>(null);
  const { enqueueSnackbar } = useExtendedSnackbar();
  const { t } = useLocalization();
  const payment = isAuthenticated ? query.data : undefined;

  useEffect(() => {
    if (payment?.status !== FINPSP_PAYMENT_STATUS.APPROVED || refreshed.current === payment.transactionId) {
      return;
    }

    refreshed.current = payment.transactionId;
    void invalidate([...FINPSP_PAYMENT_QUERY_KEYS, QUERY_KEYS.firstDepositBonus, QUERY_KEYS.welcomeBonusAvailability]);
  }, [payment?.status, payment?.transactionId, invalidate]);

  const copy = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      return true;
    } catch {
      enqueueSnackbar(t('copyFailed'), { variant: 'error' });
      return false;
    }
  };

  return {
    payment,
    copy,
    isError: query.isError,
    refetch: query.refetch,
    checkoutUrl:
      payment && getFinpspDepositType(payment) === FINPSP_DEPOSIT_TYPE.P2C && !isFinpspTerminal(payment)
        ? getFinpspCheckoutUrl(payment.redirect_url)
        : null,
    showInstructions:
      !!payment?.sim_number?.trim() && !isFinpspTerminal(payment) && payment?.state !== FINPSP_PAYMENT_STATE.REVIEW,
  };
};
